import type { Vec } from '@/lib/uploader/geometry'
import type { MakeHooks, MissKind, Motion, Pose } from '@/lib/uploader/throw'

import { NetMotion, netPaths } from '@/lib/uploader/net'
import { clamp, popScale } from '@/lib/uploader/math'

import {
    BACKBOARD_CENTER,
    FILE_SIZE,
    REST,
    TRAIL_CANVAS,
    restPointFor
} from '@/lib/uploader/geometry'

import {
    SLING,
    fadeMotion,
    ghostMotion,
    makeMotion,
    planSling,
    previewPath,
    slingMotion,
    slingVelocity
} from '@/lib/uploader/throw'

export interface ShotHooks {
    onEntry: () => void
    onScore: () => void
    onRow: () => void
    onDone: () => void
}

export interface EngineElements {
    frame: HTMLElement
    demo: HTMLElement
    canvas: HTMLCanvasElement
    net: Array<SVGPathElement | null>
    backboard: SVGGElement
    rimBack: SVGGElement
    rimFront: SVGGElement
}

export type MissSound = MissKind | 'air'

export interface EngineCallbacks {
    demoShot: () => ShotHooks
    demoMiss: (kind: MissSound) => void
    demoMissed: () => void
}

type DemoMode = 'rest' | 'drag' | 'motion' | 'gone' | 'pop'

interface Flight {
    motion: Motion
    start: number
    fired: number
    nextDot: number
    lastDot: Vec | null
    layer: number
}

interface Shot {
    el: HTMLElement | null
    flight: Flight
}

interface Dot {
    x: number
    y: number
    radius: number
    born: number
}

const HALF_WIDTH = FILE_SIZE.width / 2
const HALF_HEIGHT = FILE_SIZE.height / 2

// Stacking inside the panel: behind the front lip of the rim, in front of everything, or held.
const LAYER = {
    behind: 4,
    front: 10,
    held: 11
} as const

const DOT_STEP = 0.024
const DOT_GAP = 9
const DOT_LIFE = 600
const POP_MS = 420

const transformFor = (x: number, y: number, rotation: number, scale: number): string =>
    `translate3d(${(x - HALF_WIDTH).toFixed(2)}px, ${(y - HALF_HEIGHT).toFixed(2)}px, 0) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(4)})`

/*
 * The uploader's motion engine. It owns one requestAnimationFrame loop that runs only while
 * something moves: the held or flying demo file, real files on their way to the hoop, the
 * trail dots, the net, the rim and the backboard. React renders the elements once; the
 * engine writes their transforms directly so nothing re-renders at 60fps.
 */
export class HoopEngine {
    private readonly els: EngineElements
    private readonly callbacks: EngineCallbacks
    private readonly context: CanvasRenderingContext2D | null
    private readonly trailColor: string
    private readonly net = new NetMotion()
    private readonly shots = new Map<string, Shot>()
    private readonly timers = new Set<number>()
    private dots: Array<Dot> = []
    private scale = 1
    private canvasRatio = 1
    private canvasDirty = false
    private reduced = false
    private raf = 0
    private lastFrame = 0
    private destroyed = false
    private netDirty = false
    private rimMoved = false
    private boardMoved = false
    private vibration = { start: -1, amplitude: 0, duration: 0 }
    private flinch = { start: -1, amplitude: 0 }

    private mode: DemoMode = 'rest'
    private position: Vec = { ...REST }
    private rest: Vec = { ...REST }
    private velocity: Vec = { x: 0, y: 0 }
    private target: Vec = { ...REST }
    private grabOffset: Vec = { x: 0, y: 0 }
    private anchor: Vec = { ...REST }
    private aim: Array<Vec> = []
    private tilt = 0
    private tiltSpeed = 0
    private flight: Flight | null = null
    private popStart = 0

    constructor(els: EngineElements, callbacks: EngineCallbacks) {
        this.els = els
        this.callbacks = callbacks
        this.context = els.canvas.getContext('2d')
        this.trailColor = getComputedStyle(document.documentElement).getPropertyValue('--color-hoop').trim() || '#E8622A'
        this.setDemoState('rest')
        this.els.demo.style.zIndex = String(LAYER.front)
        this.placeDemo(0, 1, 1)
        this.resizeCanvas()
    }

    setScale(scale: number): void {
        if (scale <= 0) return

        this.scale = scale
        this.resizeCanvas()
    }

    setReducedMotion(reduced: boolean): void {
        this.reduced = reduced

        if (reduced) {
            this.dots = []
            this.canvasDirty = true
            this.wake()
        }
    }

    setRowCount(rows: number): void {
        this.rest = restPointFor(rows)

        if (this.mode === 'rest') this.wake()
    }

    restPoint(): Vec {
        return { ...this.rest }
    }

    pointFromClient(clientX: number, clientY: number): Vec {
        return this.toDesign(clientX, clientY, this.els.frame.getBoundingClientRect())
    }

    grab(event: PointerEvent): boolean {
        if (this.mode !== 'rest' || event.button !== 0) return false

        const point = this.pointFromClient(event.clientX, event.clientY)

        this.mode = 'drag'
        this.velocity = { x: 0, y: 0 }
        this.grabOffset = { x: point.x - this.position.x, y: point.y - this.position.y }
        this.anchor = { ...this.position }
        this.target = { ...this.position }
        this.aim = []
        this.tilt = 0
        this.tiltSpeed = 0
        this.setDemoState('drag')
        this.els.demo.style.zIndex = String(LAYER.held)
        document.documentElement.classList.add('nbn-grabbing')
        this.wake()

        return true
    }

    drag(event: PointerEvent): void {
        if (this.mode !== 'drag') return

        const point = this.pointFromClient(event.clientX, event.clientY)
        const dx = point.x - this.grabOffset.x - this.anchor.x
        const dy = point.y - this.grabOffset.y - this.anchor.y
        const length = Math.hypot(dx, dy)

        // Like a slingshot band, the file can only be pulled so far from where it was picked up.
        const reach = length > SLING.maxPull ? SLING.maxPull / length : 1

        this.target = { x: this.anchor.x + dx * reach, y: this.anchor.y + dy * reach }
    }

    // Letting go fires the file along the dotted line; a tiny pull, or a cancelled one, just snaps it back.
    release(_event: PointerEvent | null, cancelled = false): void {
        if (this.mode !== 'drag') return

        document.documentElement.classList.remove('nbn-grabbing')
        this.position = { ...this.target }
        this.clearAim()

        const pull = { x: this.anchor.x - this.position.x, y: this.anchor.y - this.position.y }

        if (cancelled || Math.hypot(pull.x, pull.y) < SLING.minPull) {
            this.mode = 'rest'
            this.setDemoState('rest')
            this.els.demo.style.zIndex = String(LAYER.front)
            this.wake()

            return
        }

        this.launchDemo(slingVelocity(pull))
    }

    // Keyboard equivalent of a good shot.
    shootDemo(): void {
        if (this.mode !== 'rest') return

        const from = { ...this.position }
        const hooks = this.demoMakeHooks()

        this.startDemoMotion(this.reduced
            ? ghostMotion(hooks, from)
            : makeMotion({ from, rotation: 0, scale: 1, spawn: 0, hooks }))
    }

    // Real files take the stage: the demo file steps aside until it is called back.
    hideDemo(): void {
        if (this.mode !== 'rest') return

        this.startDemoMotion(fadeMotion({ ...this.position }, () => {
            this.flight = null
            this.mode = 'gone'
            this.els.demo.style.opacity = '0'
        }), 'gone')
    }

    // True while the file is in hand or being pulled, so a newly loaded file can take its place at once.
    isDemoReady(): boolean {
        return this.mode === 'rest' || this.mode === 'drag' || this.mode === 'pop'
    }

    returnDemo(): void {
        if (this.mode === 'gone') this.startPop()
    }

    shoot(id: string, from: Vec, hooks: ShotHooks): void {
        const make = this.makeHooks(hooks, () => this.shots.delete(id))
        const motion = this.reduced
            ? ghostMotion(make, null)
            : makeMotion({ from, rotation: -6, scale: 1, spawn: 0.16, hooks: make })

        this.shots.set(id, { el: null, flight: this.createFlight(motion, -1) })
    }

    // Called from a React ref. The flight's clock starts once its element exists.
    attach(id: string, el: HTMLElement | null): void {
        const shot = this.shots.get(id)

        if (!shot) return

        if (!el) {
            shot.el = null

            return
        }

        if (shot.el === el) return

        shot.el = el

        if (shot.flight.start < 0) shot.flight.start = performance.now()

        this.wake()
    }

    destroy(): void {
        this.destroyed = true

        if (this.raf) cancelAnimationFrame(this.raf)

        this.raf = 0
        this.timers.forEach(timer => window.clearTimeout(timer))
        this.timers.clear()
        document.documentElement.classList.remove('nbn-grabbing')
    }

    // The file flies exactly along the path the dotted line showed while it was pulled back.
    private launchDemo(velocity: Vec): void {
        const from = { ...this.position }
        const plan = planSling(from, velocity)

        if (this.reduced) {
            if (plan.outcome !== 'make') this.callbacks.demoMiss(plan.outcome)

            this.startDemoMotion(plan.outcome === 'make'
                ? ghostMotion(this.demoMakeHooks(), from)
                : fadeMotion(from, () => this.missed(true)))

            return
        }

        this.startDemoMotion(slingMotion({
            from,
            velocity,
            rotation: this.tilt,
            scale: 1,
            plan,
            make: plan.outcome === 'make' ? this.demoMakeHooks() : this.idleMakeHooks(),
            miss: {
                onContact: kind => {
                    this.contact(kind)
                    this.callbacks.demoMiss(kind)
                },
                onAirball: () => this.callbacks.demoMiss('air'),
                onDone: () => this.missed(true)
            }
        }))
    }

    // Only made shots create an upload, so a miss never asks the page for one.
    private idleMakeHooks(): MakeHooks {
        const idle = (): void => undefined

        return { onEntry: idle, onRelease: idle, onScore: idle, onRow: idle, onDone: idle }
    }

    // While the file is pulled back, recompute the flight it would take and lay it out as dots.
    private updateAim(): void {
        const pull = { x: this.anchor.x - this.position.x, y: this.anchor.y - this.position.y }

        this.aim = Math.hypot(pull.x, pull.y) < SLING.minPull
            ? []
            : previewPath(planSling(this.position, slingVelocity(pull)))
        this.canvasDirty = true
    }

    private clearAim(): void {
        if (this.aim.length === 0) return

        this.aim = []
        this.canvasDirty = true
        this.wake()
    }

    private demoMakeHooks(): MakeHooks {
        return this.makeHooks(this.callbacks.demoShot(), () => {
            this.flight = null
            this.mode = 'gone'
            this.setDemoState('gone')
            this.els.demo.style.opacity = '0'
        })
    }

    private makeHooks(hooks: ShotHooks, finish: () => void): MakeHooks {
        return {
            onEntry: () => {
                this.vibrate(1, 150)
                hooks.onEntry()
            },
            onRelease: push => this.net.release(push, performance.now()),
            onScore: hooks.onScore,
            onRow: hooks.onRow,
            onDone: () => {
                finish()
                hooks.onDone()
            }
        }
    }

    private contact(kind: MissKind): void {
        this.vibrate(kind === 'board' ? 1 : 1.5, kind === 'board' ? 160 : 220)
        this.startFlinch(kind === 'board' ? 1 : 0.5)
    }

    private missed(announce: boolean): void {
        this.flight = null
        this.mode = 'gone'
        this.setDemoState('gone')
        this.els.demo.style.opacity = '0'

        if (announce) this.callbacks.demoMissed()

        this.later(() => {
            if (this.mode === 'gone') this.startPop()
        }, 240)
    }

    private startDemoMotion(motion: Motion, state: 'motion' | 'gone' = 'motion'): void {
        this.mode = 'motion'
        this.flight = this.createFlight(motion, performance.now())
        this.setDemoState(state)
        this.wake()
    }

    private startPop(): void {
        this.mode = 'pop'
        this.popStart = performance.now()
        this.position = { ...this.rest }
        this.velocity = { x: 0, y: 0 }
        this.setDemoState('pop')
        this.els.demo.style.zIndex = String(LAYER.front)
        this.placeDemo(0, this.reduced ? 1 : 0.6, 0)
        this.wake()
    }

    private createFlight(motion: Motion, start: number): Flight {
        return {
            motion,
            start,
            fired: 0,
            nextDot: motion.trail ? motion.trail[0] : 0,
            lastDot: null,
            layer: -1
        }
    }

    private wake(): void {
        if (this.raf || this.destroyed) return

        this.lastFrame = performance.now()
        this.raf = requestAnimationFrame(this.frame)
    }

    private frame = (now: number): void => {
        this.raf = 0

        if (this.destroyed) return

        const dt = clamp((now - this.lastFrame) / 1000, 0, 0.05)

        this.lastFrame = now

        let busy = this.stepDemo(now, dt)

        for (const shot of this.shots.values()) {
            if (!shot.el || shot.flight.start < 0) continue
            if (!this.runFlight(shot.flight, shot.el, now)) busy = true
        }

        if (this.stepHoop(now)) busy = true
        if (this.drawDots(now)) busy = true
        if (busy) this.raf = requestAnimationFrame(this.frame)
    }

    private stepDemo(now: number, dt: number): boolean {
        if (this.mode === 'rest') return this.settleDemo(dt)

        if (this.mode === 'drag') {
            const previous = this.position

            this.position = { ...this.target }

            if (dt > 0) {
                const speed = (this.position.x - previous.x) / dt

                this.tiltSpeed += (speed - this.tiltSpeed) * (1 - Math.exp(-dt / 0.06))
            }

            // The file leans into the direction it moves, a beat behind the hand.
            const goal = clamp(this.tiltSpeed / 55, -18, 18)

            this.tilt += (goal - this.tilt) * (1 - Math.exp(-dt / 0.1))
            this.placeDemo(this.tilt, 1, 1)
            this.updateAim()

            return true
        }

        if (this.mode === 'motion') {
            if (!this.flight) return false

            return !this.runFlight(this.flight, this.els.demo, now)
        }

        if (this.mode === 'pop') {
            const progress = (now - this.popStart) / (this.reduced ? 300 : POP_MS)

            if (progress >= 1) {
                this.mode = 'rest'
                this.setDemoState('rest')
                this.placeDemo(0, 1, 1)

                return true
            }

            this.placeDemo(0, this.reduced ? 1 : popScale(progress), clamp(progress * (this.reduced ? 1 : 2.2), 0, 1))

            return true
        }

        return false
    }

    // Springs the resting file onto its spot, with a touch of overshoot because it has weight.
    private settleDemo(dt: number): boolean {
        const dx = this.rest.x - this.position.x
        const dy = this.rest.y - this.position.y
        const still = Math.abs(dx) < 0.05 && Math.abs(dy) < 0.05 && Math.abs(this.velocity.x) < 0.5 && Math.abs(this.velocity.y) < 0.5

        if (still || this.reduced) {
            this.position = { ...this.rest }
            this.velocity = { x: 0, y: 0 }
            this.placeDemo(0, 1, 1)

            return false
        }

        this.velocity.x += (dx * 120 - this.velocity.x * 15) * dt
        this.velocity.y += (dy * 120 - this.velocity.y * 15) * dt
        this.position.x += this.velocity.x * dt
        this.position.y += this.velocity.y * dt
        this.placeDemo(0, 1, 1)

        return true
    }

    private runFlight(flight: Flight, el: HTMLElement, now: number): boolean {
        const { motion } = flight
        const elapsed = Math.max(0, (now - flight.start) / 1000)
        const t = Math.min(elapsed, motion.duration)
        const pose = motion.sample(t)

        this.applyPose(el, pose)

        const layer = pose.behind ? LAYER.behind : LAYER.front

        if (layer !== flight.layer) {
            el.style.zIndex = String(layer)
            flight.layer = layer
        }

        if (motion.trail && !this.reduced) this.emitDots(flight, Math.min(t, motion.trail[1]))
        if (motion.follow && t >= motion.follow[0] && t < motion.follow[1]) this.net.track(pose.y)

        while (flight.fired < motion.events.length && motion.events[flight.fired].at <= elapsed) {
            const event = motion.events[flight.fired]

            flight.fired += 1
            event.run()
        }

        return elapsed >= motion.duration
    }

    private applyPose(el: HTMLElement, pose: Pose): void {
        el.style.transform = transformFor(pose.x, pose.y, pose.rotation, pose.scale)
        el.style.opacity = pose.opacity >= 0.999 ? '1' : Math.max(0, pose.opacity).toFixed(3)
    }

    private placeDemo(rotation: number, scale: number, opacity: number): void {
        const el = this.els.demo

        el.style.transform = transformFor(this.position.x, this.position.y, rotation, scale)
        el.style.opacity = opacity >= 0.999 ? '1' : opacity.toFixed(3)
    }

    private setDemoState(state: DemoMode): void {
        this.els.demo.dataset.state = state
    }

    // Dots are laid along the planned path at fixed time steps, so spacing never depends on frame rate.
    private emitDots(flight: Flight, until: number): void {
        const { motion } = flight

        while (flight.nextDot <= until) {
            const pose = motion.sample(flight.nextDot)
            const last = flight.lastDot

            if (!last || Math.hypot(pose.x - last.x, pose.y - last.y) >= DOT_GAP) {
                this.dots.push({
                    x: pose.x,
                    y: pose.y,
                    radius: 2.4 * clamp(pose.scale, 0.62, 1.1),
                    born: flight.start + flight.nextDot * 1000
                })
                flight.lastDot = { x: pose.x, y: pose.y }
            }

            flight.nextDot += DOT_STEP
        }
    }

    private drawDots(now: number): boolean {
        const context = this.context

        if (!context) return false
        if (this.dots.length === 0 && this.aim.length === 0 && !this.canvasDirty) return false

        const { canvas } = this.els
        const ratio = this.canvasRatio

        context.setTransform(1, 0, 0, 1, 0, 0)
        context.clearRect(0, 0, canvas.width, canvas.height)
        this.canvasDirty = false

        if (this.dots.length === 0 && this.aim.length === 0) return false

        context.setTransform(ratio, 0, 0, ratio, -TRAIL_CANVAS.left * ratio, -TRAIL_CANVAS.top * ratio)
        context.fillStyle = this.trailColor

        // The aiming line: dots shrink and fade the further along the path they are.
        const count = this.aim.length

        this.aim.forEach((point, index) => {
            if (Math.hypot(point.x - this.position.x, point.y - this.position.y) < 24) return

            const along = count > 1 ? index / (count - 1) : 0

            context.globalAlpha = 0.9 - 0.6 * along
            context.beginPath()
            context.arc(point.x, point.y, 2.6 - 1.2 * along, 0, Math.PI * 2)
            context.fill()
        })

        let alive = 0

        for (const dot of this.dots) {
            const age = (now - dot.born) / DOT_LIFE

            if (age >= 1) continue

            this.dots[alive] = dot
            alive += 1

            if (age < 0) continue

            const life = 1 - age

            context.globalAlpha = life
            context.beginPath()
            context.arc(dot.x, dot.y, dot.radius * Math.pow(life, 0.7), 0, Math.PI * 2)
            context.fill()
        }

        this.dots.length = alive
        context.globalAlpha = 1
        this.canvasDirty = true

        return alive > 0
    }

    private stepHoop(now: number): boolean {
        const shake = this.rimShake(now)
        const vibrating = shake !== null

        if (vibrating || this.rimMoved) {
            const transform = `translate(0 ${(shake ?? 0).toFixed(2)})`

            this.els.rimBack.setAttribute('transform', transform)
            this.els.rimFront.setAttribute('transform', transform)
            this.rimMoved = vibrating
        }

        const board = this.boardFlinch(now)

        if (board !== null || this.boardMoved) {
            const amount = board ?? 0
            const { x, y } = BACKBOARD_CENTER

            this.els.backboard.setAttribute('transform', `translate(${x} ${(y - 1.6 * amount).toFixed(2)}) scale(${(1 - 0.016 * amount).toFixed(4)}) translate(${-x} ${-y})`)
            this.boardMoved = board !== null
        }

        const netActive = this.net.isActive(now)

        if (netActive || vibrating || this.netDirty) {
            const paths = netPaths(this.net.pose(now, { x: 0, y: shake ?? 0 }))

            paths.forEach((path, index) => this.els.net[index]?.setAttribute('d', path))
            this.netDirty = netActive || vibrating
        }

        return vibrating || board !== null || this.netDirty
    }

    private rimShake(now: number): number | null {
        const { start, amplitude, duration } = this.vibration

        if (start < 0) return null

        const elapsed = now - start

        if (elapsed >= duration) {
            this.vibration.start = -1

            return null
        }

        return amplitude * Math.sin(2 * Math.PI * 42 * elapsed / 1000) * (1 - elapsed / duration)
    }

    private boardFlinch(now: number): number | null {
        const { start, amplitude } = this.flinch

        if (start < 0) return null

        const t = (now - start) / 1000

        if (t >= 0.32) {
            this.flinch.start = -1

            return null
        }

        return amplitude * Math.exp(-t / 0.07) * Math.cos(2 * Math.PI * 6 * t)
    }

    private vibrate(amplitude: number, duration: number): void {
        if (this.reduced) return

        this.vibration = { start: performance.now(), amplitude, duration }
        this.wake()
    }

    private startFlinch(amplitude: number): void {
        if (this.reduced) return

        this.flinch = { start: performance.now(), amplitude }
        this.wake()
    }

    private resizeCanvas(): void {
        const ratio = this.scale * (window.devicePixelRatio || 1)
        const width = Math.max(1, Math.round(TRAIL_CANVAS.width * ratio))
        const height = Math.max(1, Math.round(TRAIL_CANVAS.height * ratio))
        const { canvas } = this.els

        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width
            canvas.height = height
        }

        this.canvasRatio = ratio
        this.canvasDirty = true
        this.wake()
    }

    private later(run: () => void, delay: number): void {
        const timer = window.setTimeout(() => {
            this.timers.delete(timer)

            if (!this.destroyed) run()
        }, delay)

        this.timers.add(timer)
    }

    private toDesign(clientX: number, clientY: number, rect: DOMRect): Vec {
        return {
            x: (clientX - rect.left) / this.scale,
            y: (clientY - rect.top) / this.scale
        }
    }
}
